// src/pages/VerifySubscriptionPage.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

export default function VerifySubscriptionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference') || searchParams.get('transactionReference');
      
      if (!reference) {
        setStatus('error');
        setMessage('No payment reference found in URL.');
        return;
      }

      try {
        const res = await api.get(`/payments/verify?reference=${reference}&gateway=korapay`);
        
        if (res.data.status === 'success') {
          setStatus('success');
          setMessage('Payment successful! Your account has been upgraded to Premium.');
          await refreshUser();
          
          setTimeout(() => {
            navigate('/dashboard/settings/profile');
          }, 3000);
        } else {
          throw new Error(res.data.message || 'Verification returned failed status.');
        }
      } catch (err) {
        console.error("Verification Error:", err);
        setStatus('error');
        setMessage(err.response?.data?.detail || err.message || 'Payment verification failed. Please contact support if you were charged.');
      }
    };

    verifyPayment();
  }, [searchParams, refreshUser, navigate]);

  return (
    // Page Background
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4 transition-colors duration-300">
      
      {/* Card Container */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-8 max-w-sm w-full text-center shadow-lg dark:shadow-none relative overflow-hidden">
        
        {/* Background Gradient Effect - Subtle gold tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/10 dark:to-orange-900/10 pointer-events-none z-0"></div>

        <div className="relative z-10">
          {status === 'loading' && (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="animate-spin text-yellow-500 dark:text-yellow-400" size={48} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Verifying Payment</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Please wait while we confirm your transaction...</p>
              <div className="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div className="bg-yellow-500 h-1.5 rounded-full animate-pulse" style={{width: '70%'}}></div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="bg-green-100 dark:bg-green-500/20 rounded-full p-3">
                  <CheckCircle className="text-green-600 dark:text-green-400" size={48} />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Success!</h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{message}</p>
              
              {/* Inner status box */}
              <div className="bg-gray-100 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-500">Redirecting you in a moment...</p>
                <div className="flex justify-center mt-2">
                  <div className="w-4 h-4 border-2 border-green-500 dark:border-green-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="bg-red-100 dark:bg-red-500/20 rounded-full p-3">
                  <XCircle className="text-red-600 dark:text-red-400" size={48} />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Verification Failed</h2>
              
              {/* Error Box */}
              <p className="text-red-600 dark:text-red-400 text-sm mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-500/30">
                {message}
              </p>
              
              <div className="flex flex-col gap-2 mt-4">
                <button 
                  onClick={() => navigate('/dashboard/subscription')}
                  className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold rounded-lg text-sm transition-colors hover:opacity-90 shadow-md"
                >
                  Try Again
                </button>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg text-sm transition-colors border border-gray-200 dark:border-none"
                >
                  Go to Dashboard
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}